import { PARKING_STATUS, TRIP_STATUS } from "./constants";
import { LocationHistory, ParkingSession, Trip } from "./types";

// Helper function to create dates relative to now
const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

const hoursAgo = (hours: number): Date => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date;
};

const minutesAgo = (minutes: number): Date => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - minutes);
  return date;
};

// Mock addresses for parking locations
const mockAddresses = [
  "123 Main Street, Downtown",
  "456 Oak Avenue, City Center",
  "789 Pine Road, Business District",
  "321 Elm Street, Shopping Mall",
  "654 Maple Lane, University Area",
  "987 Cedar Drive, Airport",
  "147 Birch Way, Hospital",
  "258 Willow Court, Stadium",
];

// Mock Parking Sessions
export const mockParkingSessions: Array<ParkingSession> = [
  {
    id: "parking-1",
    deviceId: "device-123",
    locationId: "location-1",
    startTime: hoursAgo(2),
    endTime: undefined,
    status: PARKING_STATUS.ACTIVE,
    location: {
      latitude: 52.2297,
      longitude: 21.0122,
      accuracy: 5,
      timestamp: hoursAgo(2),
    },
    cost: 8.5,
    paymentMethod: "Credit Card",
  },
  {
    id: "parking-2",
    deviceId: "device-123",
    locationId: "location-2",
    startTime: daysAgo(1),
    endTime: new Date(daysAgo(1).getTime() + 3 * 60 * 60 * 1000), // 3 hours later
    status: PARKING_STATUS.STOPPED,
    location: {
      latitude: 52.2297,
      longitude: 21.0122,
      accuracy: 8,
      timestamp: daysAgo(1),
    },
    cost: 12.75,
    paymentMethod: "Mobile App",
  },
  {
    id: "parking-3",
    deviceId: "device-123",
    locationId: "location-3",
    startTime: daysAgo(2),
    endTime: new Date(daysAgo(2).getTime() + 1.5 * 60 * 60 * 1000), // 1.5 hours later
    status: PARKING_STATUS.STOPPED,
    location: {
      latitude: 52.228,
      longitude: 21.0067,
      accuracy: 12,
      timestamp: daysAgo(2),
    },
    cost: 6.25,
    paymentMethod: "Credit Card",
  },
  {
    id: "parking-4",
    deviceId: "device-123",
    startTime: daysAgo(3),
    endTime: new Date(daysAgo(3).getTime() + 4 * 60 * 60 * 1000), // 4 hours later
    status: PARKING_STATUS.STOPPED,
    location: {
      latitude: 52.2319,
      longitude: 21.0067,
      accuracy: 6,
      timestamp: daysAgo(3),
    },
    cost: 15.0,
    paymentMethod: "Cash",
  },
  {
    id: "parking-5",
    deviceId: "device-123",
    locationId: "location-4",
    startTime: daysAgo(5),
    endTime: new Date(daysAgo(5).getTime() + 2 * 60 * 60 * 1000), // 2 hours later
    status: PARKING_STATUS.STOPPED,
    location: {
      latitude: 52.2356,
      longitude: 21.0089,
      accuracy: 10,
      timestamp: daysAgo(5),
    },
    cost: 9.5,
    paymentMethod: "Mobile App",
  },
  {
    id: "parking-6",
    deviceId: "device-123",
    startTime: daysAgo(7),
    endTime: new Date(daysAgo(7).getTime() + 6 * 60 * 60 * 1000), // 6 hours later
    status: PARKING_STATUS.STOPPED,
    location: {
      latitude: 52.2398,
      longitude: 21.0129,
      accuracy: 15,
      timestamp: daysAgo(7),
    },
    cost: 22.5,
    paymentMethod: "Credit Card",
  },
];

// Mock Trips
export const mockTrips: Array<Trip> = [
  {
    id: "trip-1",
    deviceId: "device-123",
    startTime: hoursAgo(3),
    endTime: undefined,
    status: TRIP_STATUS.ACTIVE,
    startLocation: {
      latitude: 52.2297,
      longitude: 21.0122,
      timestamp: hoursAgo(3),
    },
    distance: 15200, // 15.2 km
    averageSpeed: 35,
    maxSpeed: 65,
  },
  {
    id: "trip-2",
    deviceId: "device-123",
    startTime: daysAgo(1),
    endTime: new Date(daysAgo(1).getTime() + 45 * 60 * 1000), // 45 minutes later
    status: TRIP_STATUS.COMPLETED,
    startLocation: {
      latitude: 52.228,
      longitude: 21.0067,
      timestamp: daysAgo(1),
    },
    endLocation: {
      latitude: 52.2297,
      longitude: 21.0122,
      timestamp: new Date(daysAgo(1).getTime() + 45 * 60 * 1000),
    },
    distance: 8500, // 8.5 km
    duration: 45 * 60, // 45 minutes in seconds
    averageSpeed: 28,
    maxSpeed: 55,
  },
  {
    id: "trip-3",
    deviceId: "device-123",
    startTime: daysAgo(2),
    endTime: new Date(daysAgo(2).getTime() + 25 * 60 * 1000), // 25 minutes later
    status: TRIP_STATUS.COMPLETED,
    startLocation: {
      latitude: 52.2319,
      longitude: 21.0067,
      timestamp: daysAgo(2),
    },
    endLocation: {
      latitude: 52.228,
      longitude: 21.0067,
      timestamp: new Date(daysAgo(2).getTime() + 25 * 60 * 1000),
    },
    distance: 12300, // 12.3 km
    duration: 25 * 60, // 25 minutes in seconds
    averageSpeed: 42,
    maxSpeed: 70,
  },
  {
    id: "trip-4",
    deviceId: "device-123",
    startTime: daysAgo(4),
    endTime: new Date(daysAgo(4).getTime() + 1.5 * 60 * 60 * 1000), // 1.5 hours later
    status: TRIP_STATUS.COMPLETED,
    startLocation: {
      latitude: 52.2356,
      longitude: 21.0089,
      timestamp: daysAgo(4),
    },
    endLocation: {
      latitude: 52.2319,
      longitude: 21.0067,
      timestamp: new Date(daysAgo(4).getTime() + 1.5 * 60 * 60 * 1000),
    },
    distance: 45600, // 45.6 km
    duration: 1.5 * 60 * 60, // 1.5 hours in seconds
    averageSpeed: 38,
    maxSpeed: 85,
  },
  {
    id: "trip-5",
    deviceId: "device-123",
    startTime: daysAgo(6),
    endTime: new Date(daysAgo(6).getTime() + 20 * 60 * 1000), // 20 minutes later
    status: TRIP_STATUS.COMPLETED,
    startLocation: {
      latitude: 52.2398,
      longitude: 21.0129,
      timestamp: daysAgo(6),
    },
    endLocation: {
      latitude: 52.2356,
      longitude: 21.0089,
      timestamp: new Date(daysAgo(6).getTime() + 20 * 60 * 1000),
    },
    distance: 6700, // 6.7 km
    duration: 20 * 60, // 20 minutes in seconds
    averageSpeed: 33,
    maxSpeed: 50,
  },
];

// Mock Location History Points
export const mockLocationHistory: Array<LocationHistory> = [
  {
    id: "loc-1",
    deviceId: "device-123",
    timestamp: minutesAgo(5),
    location: {
      latitude: 52.2297,
      longitude: 21.0122,
      accuracy: 5,
      speed: 0,
      heading: 0,
    },
    tripId: "trip-1",
  },
  {
    id: "loc-2",
    deviceId: "device-123",
    timestamp: minutesAgo(15),
    location: {
      latitude: 52.2285,
      longitude: 21.0108,
      accuracy: 8,
      speed: 25,
      heading: 45,
    },
    tripId: "trip-1",
  },
  {
    id: "loc-3",
    deviceId: "device-123",
    timestamp: minutesAgo(30),
    location: {
      latitude: 52.227,
      longitude: 21.0095,
      accuracy: 12,
      speed: 35,
      heading: 90,
    },
    tripId: "trip-1",
  },
  {
    id: "loc-4",
    deviceId: "device-123",
    timestamp: hoursAgo(1),
    location: {
      latitude: 52.225,
      longitude: 21.008,
      accuracy: 6,
      speed: 45,
      heading: 135,
    },
    tripId: "trip-1",
  },
  {
    id: "loc-5",
    deviceId: "device-123",
    timestamp: hoursAgo(2),
    location: {
      latitude: 52.223,
      longitude: 21.0065,
      accuracy: 10,
      speed: 50,
      heading: 180,
    },
    tripId: "trip-1",
  },
];

// Helper function to get address for parking session
export function getParkingAddress(session: ParkingSession): string {
  const index = parseInt(session.id.replace("parking-", "")) - 1;
  return mockAddresses[index % mockAddresses.length];
}

// Helper function to format duration
export function formatDuration(startTime: Date, endTime?: Date): string {
  const end = endTime || new Date();
  const diffMs = end.getTime() - startTime.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// Helper function to format distance
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${meters} m`;
}

// Helper function to format speed
export function formatSpeed(kmh: number): string {
  return `${kmh} km/h`;
}

// Helper function to format relative time
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  } else {
    return "Just now";
  }
}
